// Touch play: whether the player is using a touchscreen (the last press decides, so hybrid laptops switch both ways),
// and the on-screen keyboard (simple-keyboard) that types into the focused TextInput while touching.
import { SimpleKeyboard as Keyboard } from 'simple-keyboard';
import { focus, TextInput } from './widget';

class TouchMode {
  active = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  readonly changed = new Set<(active: boolean) => void>();
  install(): void {
    window.addEventListener('pointerdown', e => this.set(e.pointerType !== 'mouse'), { capture: true });
  }
  private set(active: boolean): void {
    if (active === this.active) return;
    this.active = active;
    for (const fn of this.changed) fn(active);
  }
}
export const touchMode = new TouchMode();

const LAYOUT = {
  default: [
    '1 2 3 4 5 6 7 8 9 0 {bksp}',
    'q w e r t y u i o p',
    'a s d f g h j k l \'',
    '{shift} z x c v b n m , . ? !',
    '{hide} {left} / {space} - {right} {enter}',
  ],
  shift: [
    '@ # $ % & * ( ) _ + {bksp}',
    'Q W E R T Y U I O P',
    'A S D F G H J K L "',
    '{shift} Z X C V B N M ; : ? !',
    '{hide} {left} / {space} = {right} {enter}',
  ],
};
const DISPLAY = { '{bksp}': '⌫', '{shift}': '⇧', '{space}': ' ', '{enter}': 'Enter', '{hide}': '⌄', '{left}': '←', '{right}': '→' };
const KEYS: Record<string, string> = { '{bksp}': 'Backspace', '{enter}': 'Enter', '{space}': ' ', '{left}': 'ArrowLeft', '{right}': 'ArrowRight' };

const element = (tag: string, className: string, parent: HTMLElement): HTMLElement => {
  const e = document.createElement(tag);
  e.className = className;
  parent.append(e);
  return e;
};

/**
 * The on-screen keyboard: while a TextInput has focus in touch mode, a full-screen overlay (so it never hides the
 * form underneath) with the field's label, a large preview of its text (tap or drag in it to move the caret) and the
 * keys. The field stays the source of truth: keys go to it as keydowns, and the preview redraws from its editor.
 */
class OnScreenKeyboard {
  private host: HTMLElement | null = null;
  private context!: HTMLElement;
  private label!: HTMLElement;
  private preview!: HTMLElement;
  private keyboard: Keyboard | null = null;
  private field: TextInput | null = null;
  private shifted = false;
  private shown = '';
  private enterLabel = '';
  private dragging = false;

  install(): void {
    focus.changed.add(() => this.refresh());
    touchMode.changed.add(() => this.refresh());
  }

  private refresh(): void {
    const field = focus.current instanceof TextInput && touchMode.active ? focus.current : null;
    if (field === this.field) return;
    this.field = field;
    if (!field) { this.host?.classList.add('hidden'); return; }
    this.create().classList.remove('hidden');
    this.setShift(false);
    this.shown = '';
    this.render();
  }

  private create(): HTMLElement {
    if (this.host) return this.host;
    const host = this.host = element('div', 'touch-keyboard hidden', document.body);
    // Presses on the overlay must not reach the canvas (that would blur the field) nor the joysticks. A press on the
    // backdrop (not the preview or keys) closes the keyboard, keeping what was typed.
    host.addEventListener('pointerdown', e => {
      e.stopPropagation(); e.preventDefault();
      if (e.target === host || e.target === this.context) focus.set(null);
    });
    this.context = element('div', 'touch-keyboard-context', host);
    const entry = element('div', 'touch-keyboard-entry', host);
    this.label = element('div', 'touch-keyboard-label', entry);
    this.preview = element('div', 'touch-keyboard-preview', entry);
    this.preview.addEventListener('pointerdown', e => { this.dragging = true; this.preview.setPointerCapture(e.pointerId); this.placeCaret(e.clientX); });
    this.preview.addEventListener('pointermove', e => { if (this.dragging) this.placeCaret(e.clientX); });
    for (const end of ['pointerup', 'pointercancel'] as const) this.preview.addEventListener(end, () => { this.dragging = false; });
    // simple-keyboard requires its element to have a class.
    const inner = element('div', 'simple-keyboard', host);
    this.keyboard = new Keyboard(inner, {
      layout: LAYOUT, display: DISPLAY, theme: 'hg-theme-default ash-keyboard',
      preventMouseDownDefault: true, disableButtonHold: false,
      onKeyPress: (button: string) => this.press(button),
    });
    const frame = () => { if (this.field) this.render(); requestAnimationFrame(frame); };
    requestAnimationFrame(frame); // follows a physical keyboard, Enter's effects (chat channels) and the field's own changes
    return host;
  }

  /** The next text field in the same panel (sign-in: email, then password), which Enter moves to; the last submits. */
  private nextField(field: TextInput): TextInput | null {
    const group = field.parentWidget;
    if (!group) return null;
    const fields = group.querySelectorAll('*').filter((w): w is TextInput => w instanceof TextInput && w.isShown && !w.disabled);
    return fields[fields.indexOf(field) + 1] ?? null;
  }

  private render(): void {
    const field = this.field!;
    if (!field.isConnected) { focus.set(null); return; }
    const { editor } = field, text = field.display();
    const context = field.keyboardContext?.() ?? [];
    const enter = this.nextField(field) ? 'Next' : 'Enter';
    const key = [field.label, field.placeholder, text, editor.start, editor.end, editor.caret, editor.maxLength, enter,
      ...context.map(l => `${l.color}\u0001${l.text}`)].join('\u0000');
    if (key === this.shown) return;
    this.shown = key;
    if (enter !== this.enterLabel) { this.enterLabel = enter; this.keyboard?.setOptions({ display: { ...DISPLAY, '{enter}': enter } }); }
    this.context.replaceChildren(...context.map(l => {
      const line = document.createElement('div');
      line.textContent = l.text; line.style.color = l.color;
      return line;
    }));
    this.label.textContent = field.label || field.placeholder || 'Text';
    const count = document.createElement('span');
    count.className = 'touch-keyboard-count';
    count.textContent = `${editor.text.length}/${editor.maxLength}`;
    this.label.append(count);
    // One span per character, so a tap finds its nearest boundary; the caret is a zero-width bar at its index.
    const chars = [...text].map((c, i) => {
      const span = document.createElement('span');
      span.textContent = c;
      if (i >= editor.start && i < editor.end) span.className = 'selected';
      return span;
    });
    const caret = document.createElement('span');
    caret.className = 'touch-keyboard-caret';
    chars.splice(editor.caret, 0, caret);
    if (!text && field.placeholder) {
      const hint = document.createElement('span');
      hint.className = 'touch-keyboard-placeholder'; hint.textContent = field.placeholder;
      chars.push(hint);
    }
    this.preview.replaceChildren(...chars);
    caret.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** Moves the caret to the character boundary nearest a screen x in the preview. */
  private placeCaret(x: number): void {
    const field = this.field;
    if (!field) return;
    const spans = Array.from(this.preview.children).filter(e => !e.classList.contains('touch-keyboard-caret') && !e.classList.contains('touch-keyboard-placeholder'));
    let index = spans.length;
    for (let i = 0; i < spans.length; i++) {
      const r = spans[i].getBoundingClientRect();
      if (x < r.left + r.width / 2) { index = i; break; }
    }
    field.editor.select(index);
    field.invalidate();
    this.render();
  }

  private press(button: string): void {
    const field = this.field;
    if (button === '{shift}') { this.setShift(!this.shifted); return; }
    if (button === '{hide}') { focus.set(null); return; }
    if (button === '{enter}' && field) {
      const next = this.nextField(field);
      if (next) { next.focus(); return; }
    }
    const key = KEYS[button] ?? button;
    // The focus manager's capture listener hands window keydowns to the focused TextInput (Enter submits, etc.).
    window.dispatchEvent(new KeyboardEvent('keydown', { key, code: key === 'Enter' || key === 'Backspace' || key.startsWith('Arrow') ? key : '', cancelable: true }));
    if (this.shifted && key.length === 1) this.setShift(false);
    if (this.field) this.render();
  }

  private setShift(on: boolean): void {
    this.shifted = on;
    this.keyboard?.setOptions({ layoutName: on ? 'shift' : 'default' });
  }
}
export const onScreenKeyboard = new OnScreenKeyboard();
